import classNames from "classnames";
import { useLayoutEffect, useRef, useState } from "react";
import { useUsers } from "../../api/users";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useDebounce } from "../../hooks/useDebounce";
import { useTranslation } from "../../hooks/useTranslation";
import type { UserLightResponse } from "../../types/user";
import styles from "./UserSelect.module.css";

interface UserSelectProps {
  selected?: UserLightResponse[];
  onChange: (users: UserLightResponse[]) => void;
  label?: string;
  excludeIds?: string[];
}

export function UserSelect({ selected = [], onChange, label, excludeIds = [] }: UserSelectProps) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [dropUp, setDropUp] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useClickOutside(rootRef, () => setIsOpen(false));

  const { data: usersPage, isLoading } = useUsers({ search: debouncedSearch }, debouncedSearch.length > 0);
  const users = usersPage?.data ?? [];

  useLayoutEffect(() => {
    if (!inputWrapperRef.current || !dropdownRef.current) return;
    const inputRect = inputWrapperRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current.offsetHeight;
    setDropUp(window.innerHeight - inputRect.bottom < dropdownHeight);
  }, [debouncedSearch, isLoading]);

  const filtered = users.filter((u) => !excludeIds.includes(u.id));

  function toggle(user: UserLightResponse) {
    if (selected.some((u) => u.id === user.id)) {
      onChange(selected.filter((u) => u.id !== user.id));
    } else {
      onChange([...selected, user]);
    }
  }

  return (
    <div className={styles["root"]} ref={rootRef}>
      {label && <span className={styles["label"]}>{label}</span>}

      {selected.length > 0 && (
        <div className={styles["selectedList"]}>
          {selected.map((u) => (
            <span key={u.id} className={styles["selectedItem"]}>
              {u.firstName} {u.lastName}
              <button
                type="button"
                onClick={() => onChange(selected.filter((s) => s.id !== u.id))}
                className={styles["removeButton"]}
                aria-label={`Remove ${u.firstName} ${u.lastName}`}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={styles["inputWrapper"]} ref={inputWrapperRef}>
        <input
          type="text"
          placeholder={t("common.search")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          className={styles["searchInput"]}
        />
        {isOpen && (isLoading || debouncedSearch.length > 0) && (
          <div ref={dropdownRef} className={classNames(styles["dropdown"], dropUp && styles["dropdownAbove"])}>
            {isLoading ? (
              <div className={styles["loadingText"]}>{t("common.loading")}</div>
            ) : (
              <ul className={styles["results"]}>
                {filtered.length === 0 && <li className={styles["emptyText"]}>{t("common.noData")}</li>}
                {filtered.map((u) => {
                  const isSelected = selected.some((s) => s.id === u.id);
                  return (
                    <li key={u.id} className={styles["resultItem"]}>
                      <button
                        type="button"
                        onClick={() => toggle(u)}
                        className={classNames(styles["resultButton"], isSelected && styles["resultButtonSelected"])}
                      >
                        <span className={styles["resultName"]}>
                          {u.firstName} {u.lastName}
                          <span className={styles["resultEmail"]}>({u.email})</span>
                        </span>
                        {isSelected && <span className={styles["check"]}>&#10003;</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
